import type { ILocationResult } from '@components/searchCity';
import type { LeagueEvent } from '@interfaces/APITravel';
import { MarkerPin01 } from '@v2/ui';
import type { FormikProps } from 'formik';
import { useEffect, useState } from 'react';
import {
  coerceHotelSearchArea,
  hotelAreaChipLabel,
  stadiumLocationFromEvent,
  type HotelSearchArea,
} from '../../../hotel-search-area';
import { useHotelSearchArea } from '../../../hooks/use-hotel-search-area';
import { HotelAreaDialog } from '../../HotelAreaDialog';
import { HotelAreaPicker } from '../../HotelAreaPicker';
import { HeaderFormikProps } from '../Header';
import { InputWithLabel } from './InputWithLabel';

export function BaseLocationFilter(props: {
  formik: FormikProps<HeaderFormikProps>;
  event: LeagueEvent;
  variant?: 'dialog' | 'inline';
  onApplyArea?: (next: {
    area: HotelSearchArea;
    location: ILocationResult;
  }) => void;
}) {
  const {
    formik: { values, setFieldValue },
    event,
    variant = 'dialog',
    onApplyArea,
  } = props;
  const { downtown } = useHotelSearchArea(event);
  const area = coerceHotelSearchArea(values.hotelSearchArea);
  const stadium =
    values.stadiumLocation ?? stadiumLocationFromEvent(event) ?? null;
  const city =
    event.venue?.address?.locality ||
    event.venue?.location ||
    values.location?.city_name ||
    '';
  const chip = hotelAreaChipLabel({
    area,
    venueName: event.venue?.name,
    city,
    customName: values.location?.name,
  });
  const [open, setOpen] = useState(false);
  const [customText, setCustomText] = useState(values.location?.name ?? '');

  useEffect(() => {
    const name = values.location?.name;
    if (name) setCustomText(name);
  }, [values.location?.name]);

  const writeDraft = (next: {
    area: HotelSearchArea;
    location: ILocationResult | null;
  }) => {
    setFieldValue('hotelSearchArea', next.area);
    setFieldValue('location', next.location);
    setCustomText(next.location?.name ?? '');
  };

  if (variant === 'inline') {
    return (
      <HotelAreaPicker
        event={event}
        downtown={downtown}
        value={area}
        customQuery={customText}
        showLandmarkSearch
        onSelectArea={(next) => {
          writeDraft({
            area: next,
            location: next === 'downtown' ? downtown : stadium,
          });
        }}
        onSelectCustom={(location) => {
          writeDraft({ area: 'custom', location });
        }}
        onCustomQueryChange={(next) => {
          setCustomText(next);
          setFieldValue('hotelSearchArea', 'custom');
          setFieldValue('location', null);
        }}
      />
    );
  }

  return (
    <>
      <InputWithLabel
        icon={
          <MarkerPin01
            className="text-gray-800"
            pathProps={{ stroke: 'currentColor' }}
          />
        }
        label="Location"
        value={chip}
        onClick={() => setOpen(true)}
      />
      <HotelAreaDialog
        open={open}
        onOpenChange={setOpen}
        event={event}
        downtown={downtown}
        stadium={stadium}
        area={area}
        location={values.location}
        onApply={(next) => {
          writeDraft(next);
          onApplyArea?.(next);
        }}
      />
    </>
  );
}
